/**
 * OrdenStateActions - Botones de transición de estado para el detail sheet.
 * Muestra botones para transiciones válidas + dar de baja si aplica.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock, FlaskConical, CheckCircle2, Ban } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useAuth } from "@/contexts/auth-context";
import { useApiError } from "@/hooks/use-api-error";
import { useCambiarEstadoOrden, useDarDeBajaOrden } from "@/hooks/use-ordenes";
import type { Orden, EstadoOrden } from "@/hooks/use-ordenes";
import {
  getValidTransitions,
  getTransitionMeta,
  canDarDeBaja,
  isTerminalState,
} from "../utils/orden-transitions";

interface OrdenStateActionsProps {
  orden: Orden;
  onTransitionSuccess?: () => void;
}

const ICON_MAP = {
  Clock,
  FlaskConical,
  CheckCircle2,
} as const;

export function OrdenStateActions({ orden, onTransitionSuccess }: OrdenStateActionsProps) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { getErrorMessage } = useApiError();

  const [confirmTarget, setConfirmTarget] = useState<EstadoOrden | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState("");

  const canChangeStatus = hasPermission("ordenes", "change_status");
  const canCancel = hasPermission("ordenes", "cancel");

  const cambiarEstadoMutation = useCambiarEstadoOrden({
    onSuccess: () => {
      toast.success(
        t("ordenes.transitions.success", {
          numero: orden.numero,
          estado: confirmTarget,
        })
      );
      setConfirmTarget(null);
      onTransitionSuccess?.();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
      setConfirmTarget(null);
    },
  });

  const darDeBajaMutation = useDarDeBajaOrden({
    onSuccess: () => {
      toast.warning(t("ordenes.toast.cancelled"));
      setShowCancelDialog(false);
      setCancelMotivo("");
      onTransitionSuccess?.();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    },
  });

  if (isTerminalState(orden.estado)) return null;

  const transitions = canChangeStatus ? getValidTransitions(orden.estado) : [];
  const showDarDeBaja = canCancel && canDarDeBaja(orden.estado);

  if (transitions.length === 0 && !showDarDeBaja) return null;

  const targetMeta = confirmTarget ? getTransitionMeta(confirmTarget) : null;

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {transitions.map((target) => {
          const meta = getTransitionMeta(target);
          if (!meta) return null;
          const Icon = ICON_MAP[meta.icon];
          return (
            <Button
              key={target}
              variant={meta.variant}
              size="sm"
              onClick={() => setConfirmTarget(target)}
            >
              <Icon className="h-3.5 w-3.5 mr-1.5" />
              {t(meta.labelKey)}
            </Button>
          );
        })}
        {showDarDeBaja && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              setCancelMotivo("");
              setShowCancelDialog(true);
            }}
          >
            <Ban className="h-3.5 w-3.5 mr-1.5" />
            {t("ordenes.cancelOrder")}
          </Button>
        )}
      </div>

      {/* Confirm transition dialog */}
      <ConfirmDialog
        open={!!confirmTarget}
        onOpenChange={(open) => !open && setConfirmTarget(null)}
        title={t("ordenes.transitions.confirmTitle")}
        description={t("ordenes.transitions.confirmDesc", {
          numero: orden.numero,
          from: orden.estado_display,
          to: targetMeta ? t(targetMeta.labelKey) : "",
        })}
        confirmText={targetMeta ? t(targetMeta.labelKey) : t("common.confirm")}
        onConfirm={() => {
          if (confirmTarget) {
            cambiarEstadoMutation.mutate({ id: orden.id, estado: confirmTarget });
          }
        }}
        isLoading={cambiarEstadoMutation.isPending}
      />

      {/* Cancel order dialog */}
      <ConfirmDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        title={t("ordenes.confirmCancel")}
        description={
          <div className="space-y-2">
            <p>{t("ordenes.confirmCancelDesc")}</p>
            <input
              className="w-full rounded-md border px-3 py-2 text-sm"
              placeholder={t("ordenes.cancelReasonPlaceholder")}
              value={cancelMotivo}
              onChange={(e) => setCancelMotivo(e.target.value)}
            />
          </div>
        }
        variant="destructive"
        onConfirm={() => {
          darDeBajaMutation.mutate({ id: orden.id, motivo: cancelMotivo });
        }}
        isLoading={darDeBajaMutation.isPending}
      />
    </>
  );
}
